import React, { FC, useEffect, useState } from 'react';

import { Box, HStack, Stack, Text } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { customTheme } from 'theme';

import { CopyToClipboardIconButton, PrimaryButton } from 'components/DataEntry';
import { DisplayField } from 'components/DataEntry/OutlinedForm';
import {
  ExclamationTriangleIcon,
  InformationAlert,
  KeyIcon,
  OutlinedForm,
  PageHeaderWithIcon,
} from 'components/index';
import { Modal } from 'components/Overlay';

import { selectApiKeys } from '../selectors';
import { actions } from '../slice';

interface ICreateApiKeyModal {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const usageSnippet = (secret?: string) => {
  const origin = window.location.origin;
  return `# 1. Exchange the API key for an access token
curl -X POST ${origin}/v1/auth/token \\
  -H "Content-Type: application/json" \\
  -d '{"secret": "${secret}"}'
# Response: {"access_token": "<ACCESS_TOKEN>", "expires_in": <SECONDS>}

# 2. Call the API with the access token
curl ${origin}/v1/auth/keys \\
  -H "Authorization: Bearer <ACCESS_TOKEN>"`;
};

export const CreateApiKeyModal: FC<ICreateApiKeyModal> = props => {
  const { isOpen, onClose, onCreated } = props;
  const dispatch = useDispatch();
  const { createApiKey } = useSelector(selectApiKeys);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [expiresAt, setExpiresAt] = useState<Date | undefined>(undefined);

  const created = createApiKey.data;
  const isCreating = createApiKey.loading;

  useEffect(() => {
    if (isOpen) {
      dispatch(actions.resetCreateApiKey());
    }
  }, [dispatch, isOpen]);

  const reset = () => {
    setName('');
    setDescription('');
    setExpiresAt(undefined);
    dispatch(actions.resetCreateApiKey());
  };

  const handleClose = () => {
    const hadCreated = !!created;
    reset();
    onClose();
    if (hadCreated) {
      onCreated();
    }
  };

  const handleSubmit = () => {
    dispatch(
      actions.createApiKey({
        name: name.trim(),
        description: description.trim() || undefined,
        // The picker returns local midnight; keep the key usable through the chosen day.
        expiration_time: expiresAt
          ? dayjs(expiresAt).endOf('day').toISOString()
          : undefined,
      }),
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={created ? () => {} : handleClose}
      closeButton={!created}
      closeOnOverlayClick={!created}
      closeOnEsc={!created}
      header={
        <Box pl={3}>
          <PageHeaderWithIcon
            label={(!created ? 'Create' : '') + ' API Key'}
            icon={<KeyIcon />}
          />
        </Box>
      }
      styles={{
        modal: { size: created ? '3xl' : 'xl' },
        body: { pr: 4 },
      }}
      footer={
        created ? (
          <HStack w="full" justify="flex-end">
            <PrimaryButton onClick={handleClose}>Done</PrimaryButton>
          </HStack>
        ) : undefined
      }
      body={
        created ? (
          <Stack spacing={5} pl={2}>
            <Box pr={8}>
              <InformationAlert
                icon={
                  <ExclamationTriangleIcon
                    color={customTheme.colors.red['400']}
                  />
                }
                description="Please save the token somewhere safe and accessible. For security
                reasons, you won’t be able to view it again from Ariksa. If you lose
                this token, you’ll need to generate another token"
                bg="#fdf8e9"
                border="1px solid"
                borderColor={customTheme.colors.componentBorder}
                alignItems="flex-start"
              />
            </Box>
            <OutlinedForm
              fields={[
                {
                  type: 'custom',
                  name: 'api-key',
                  //label: 'API Key',
                  component: (
                    <HStack w="full" align="center" spacing={0}>
                      <DisplayField
                        field={{
                          label: 'API Key',
                          value: created?.secret,
                          type: 'display',
                          name: 'api-key-display',
                          styles: {
                            field: { w: 'full', flex: 1 },
                          },
                        }}
                      />
                      <CopyToClipboardIconButton
                        tooltip="Copy API key"
                        value={created?.secret}
                        flexShrink={0}
                      />
                    </HStack>
                  ),
                },
                {
                  type: 'display',
                  name: 'using-this-key',
                  label: 'Using this key',
                  value: (
                    <Stack w="full" spacing={2}>
                      <Text fontSize="sm">
                        Exchange this API key for an access token, then send the
                        token in the Authorization: Bearer header of your API
                        calls. The token expires after the number of seconds
                        returned in expires_in; exchange the key again to get a
                        new one.
                      </Text>
                      <Box
                        as="pre"
                        fontSize="12px"
                        fontFamily="mono"
                        bg={customTheme.colors.gray[50]}
                        borderRadius={4}
                        p={3}
                        overflowX="auto"
                        whiteSpace="pre-wrap"
                        wordBreak="break-all"
                      >
                        {usageSnippet(created?.secret)}
                      </Box>
                    </Stack>
                  ),
                  styles: { field: { h: 'auto', pb: 4, mr: 8 } },
                },
              ]}
            />
          </Stack>
        ) : (
          <Box pr={8} pl={2}>
            <OutlinedForm
              fields={[
                {
                  type: 'text',
                  name: 'name',
                  label: 'Name',
                  value: name,
                  onChange: event => setName(event.target.value),
                  inputProps: {
                    autoFocus: true,
                    isRequired: true,
                    title: 'A label to help you identify this key later',
                  },
                },
                {
                  // OutlinedForm has no textarea field; other types render nothing.
                  type: 'text',
                  name: 'description',
                  label: 'Description (optional)',
                  value: description,
                  onChange: event => setDescription(event.target.value),
                  placeholder: 'What this key is used for',
                },
                {
                  type: 'dateRange',
                  name: 'expires_at',
                  label: 'Expiration date (optional)',
                  dateProps: {
                    mode: 'single',
                    fromDate: dayjs().add(1, 'day').toDate(),
                    selected: expiresAt,
                    onSelect: selected => setExpiresAt(selected),
                  },
                },
                {
                  type: 'custom',
                  name: 'api-key-information',
                  component: (
                    <InformationAlert
                      icon={
                        <ExclamationTriangleIcon
                          color={customTheme.colors.red['400']}
                        />
                      }
                      description="Once the key is generated, please copy for use. It will be shown only once after creation"
                      bg="#fdf8e9"
                      border="1px solid"
                      borderColor={customTheme.colors.componentBorder}
                    />
                  ),
                },
              ]}
              onCancel={handleClose}
              onSubmit={event => {
                event.preventDefault();
                handleSubmit();
              }}
              submitButton={{
                label: 'CREATE',
                isLoading: isCreating,
                isDisabled: !name.trim(),
              }}
            />
          </Box>
        )
      }
    />
  );
};
